import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Search, CheckCircle2, XCircle, AlertTriangle, Clock, ChevronRight, FileText, Star, Send, ArrowLeft, CalendarDays, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  getLeadByCode, updateLead, calcGarantia, formatDate, generateLeadCode,
  getPedidosExtensao, createPedidoExtensao, ROUTE_PATHS,
  getPropostas, updateProposta, getPedidosOrcamento, getInstalaldoresComelit,
  queueEmail,
  type Lead, type Proposta, type PedidoOrcamento,
} from '@/lib/index';

type View = 'search' | 'detail' | 'extension';

export default function GuaranteePage() {
  const [view, setView] = useState<View>('search');
  const [emailOuTel, setEmailOuTel] = useState('');
  const [code, setCode] = useState('');
  const [lead, setLead] = useState<Lead | null>(null);
  const [error, setError] = useState('');
  const [invoiceInput, setInvoiceInput] = useState('');
  const [invoiceSaved, setInvoiceSaved] = useState(false);
  const [extensionMotivo, setExtensionMotivo] = useState('');
  const [extensionSent, setExtensionSent] = useState(false);
  const [existingExtension, setExistingExtension] = useState<import('@/lib/index').PedidoExtensaoGarantia | null>(null);
  const [propostas, setPropostas] = useState<Proposta[]>([]);
  const [pedidosOrc, setPedidosOrc] = useState<PedidoOrcamento[]>([]);
  const [aprovandoProposta, setAprovandoProposta] = useState<string | null>(null);
  const [agendandoData, setAgendandoData] = useState<string | null>(null); // propostaId a agendar
  const [novaDataInstalacao, setNovaDataInstalacao] = useState('');

  const handleSearch = async () => {
    setError('');
    const clean = code.trim().toUpperCase();
    const termo = emailOuTel.trim().toLowerCase();

    // Busca por email ou telefone
    if (!clean.startsWith('CTR-')) {
      if (!termo) { setError('Introduza o código CTR ou o seu email/telefone.'); return; }
      const { getLeads } = await import('@/lib/index');
      const todos = await getLeads();
      const found = todos.find(l => l.email?.toLowerCase() === termo || l.telefone?.replace(/\s/g,'') === termo.replace(/\s/g,''));
      if (!found) { setError('Não encontrámos nenhum pedido com esse email ou telefone.'); return; }
      const [todasPropostas, todosPedidos, extensoes2] = await Promise.all([getPropostas(), getPedidosOrcamento(), getPedidosExtensao()]);
      const ctr = generateLeadCode(found.id);
      setPropostas(todasPropostas.filter(p => p.refCTR === ctr));
      setPedidosOrc(todosPedidos.filter(p => p.leadId === found.id));
      setExistingExtension(extensoes2.find(e => e.leadId === found.id) ?? null);
      setLead(found);
      setInvoiceInput(found.numeroFatura ?? '');
      setView('detail');
      return;
    }
    const found = await getLeadByCode(clean);
    if (!found) {
      setError('Referência não encontrada. Verifique o código e tente novamente.');
      return;
    }
    const [extensoes, todasPropostas, todosPedidos] = await Promise.all([getPedidosExtensao(), getPropostas(), getPedidosOrcamento()]);
    setExistingExtension(extensoes.find((e) => e.leadId === found.id) ?? null);
    setLead(found);
    setInvoiceInput(found.numeroFatura ?? '');
    setView('detail');
    setPropostas(todasPropostas.filter(p => p.refCTR === clean));
    setPedidosOrc(todosPedidos.filter(p => p.leadId === found.id));
  };

  const handleAprovarProposta = async (propostaId: string) => {
    setAprovandoProposta(propostaId);
    const proposta = propostas.find(p => p.id === propostaId);
    const jaAprovada = proposta?.estadoCliente === 'aprovado';

    if (!jaAprovada && proposta) {
      await updateProposta(propostaId, { estadoCliente: 'aprovado' });
      setPropostas(prev => prev.map(p => p.id === propostaId ? { ...p, estadoCliente: 'aprovado' } : { ...p, estadoCliente: p.estadoCliente === 'aprovado' ? 'pendente' : p.estadoCliente }));

      // Notificar AT CONTERA via EmailQueue
      await queueEmail({
        email: 'comercial@contera.pt',
        nome: 'AT CONTERA',
        assunto: `[AT CONTERA] ✅ Orçamento aprovado pelo cliente — ${proposta.refCTR}`,
        mensagem: `O cliente aprovou um orçamento no portal.\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━\nReferência: ${proposta.refCTR}\nCliente: ${proposta.clienteNome || '—'}\nMorada da obra: ${proposta.obraMorada || '—'}\nInstalador: ${proposta.instaladorNome}\nTipo: ${proposta.tipoSistema === 'video' ? 'Vídeo' : 'Áudio'}${proposta.numInquilinos ? ` · ${proposta.numInquilinos} inquilinos` : ''}\nValor total: ${proposta.totalEuros.toLocaleString('pt-PT', { minimumFractionDigits: 2 })} €\nData proposta: ${new Date(proposta.criadoEm).toLocaleDateString('pt-PT')}\n━━━━━━━━━━━━━━━━━━━━━━━━━━\n\nAceda ao dashboard para processar a adjudicação.`,
      });

      // Notificar o instalador via EmailQueue
      // Tentar usar instaladorEmail da proposta; se não tiver, buscar nos instaladores
      let instaladorEmail = proposta.instaladorEmail || '';
      if (!instaladorEmail && proposta.instaladorId) {
        try {
          const instaladores = await getInstalaldoresComelit();
          const inst = instaladores.find(i => i.id === proposta.instaladorId);
          if (inst) instaladorEmail = inst.email;
        } catch (e) { /* ignorar */ }
      }
      if (instaladorEmail) {
        await queueEmail({
          email: instaladorEmail,
          nome: proposta.instaladorNome,
          assunto: `🎉 Orçamento aprovado pelo cliente — ${proposta.refCTR}`,
          mensagem: `Olá ${proposta.instaladorNome},\n\nO cliente aprovou o seu orçamento! Está uma obra a caminho.\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━\nReferência: ${proposta.refCTR}\nCliente: ${proposta.clienteNome || '—'}\nMorada: ${proposta.obraMorada || '—'}\nValor aprovado: ${proposta.totalEuros.toLocaleString('pt-PT', { minimumFractionDigits: 2 })} €\nSinal (60%): ${(proposta.totalEuros * 0.6).toLocaleString('pt-PT', { minimumFractionDigits: 2 })} €\nRestante (40%): ${(proposta.totalEuros * 0.4).toLocaleString('pt-PT', { minimumFractionDigits: 2 })} €\n━━━━━━━━━━━━━━━━━━━━━━━━━━\n\nA AT CONTERA irá contactá-lo para formalizar a adjudicação e coordenar o pagamento do sinal.\n\nAceda ao seu portal para mais detalhes:\nhttps://7egv7wu65a.skywork.website/instalador\n\nCumprimentos,\nEquipa AT CONTERA`,
        });
      }
    } else {
      await updateProposta(propostaId, { estadoCliente: 'pendente' });
      setPropostas(prev => prev.map(p => p.id === propostaId ? { ...p, estadoCliente: 'pendente' } : p));
    }
    setAprovandoProposta(null);
  };

  const handleAgendar = async (propostaId: string) => {
    if (!novaDataInstalacao) return;
    const proposta = propostas.find(p => p.id === propostaId);
    await updateProposta(propostaId, { dataInstalacao: novaDataInstalacao });
    setPropostas(prev => prev.map(p => p.id === propostaId ? { ...p, dataInstalacao: novaDataInstalacao } : p));
    setAgendandoData(null);
    setNovaDataInstalacao('');

    // Notificar AT CONTERA + instalador sobre a data proposta pelo cliente
    if (proposta) {
      const dataFormatada = new Date(novaDataInstalacao).toLocaleDateString('pt-PT', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
      await queueEmail({
        email: 'comercial@contera.pt',
        nome: 'AT CONTERA',
        assunto: `📅 Data de instalação proposta — ${proposta.refCTR}`,
        mensagem: `O cliente propôs uma data de instalação.\n\nReferência: ${proposta.refCTR}\nCliente: ${proposta.clienteNome || '—'}\nInstalador: ${proposta.instaladorNome}\nData proposta: ${dataFormatada}\n\nAceda ao dashboard para confirmar.`,
      });
      let instaladorEmail = proposta.instaladorEmail || '';
      if (!instaladorEmail && proposta.instaladorId) {
        try {
          const instaladores = await getInstalaldoresComelit();
          const inst = instaladores.find(i => i.id === proposta.instaladorId);
          if (inst) instaladorEmail = inst.email;
        } catch (e) { /* ignorar */ }
      }
      if (instaladorEmail) {
        await queueEmail({
          email: instaladorEmail,
          nome: proposta.instaladorNome,
          assunto: `📅 Cliente propôs data de instalação — ${proposta.refCTR}`,
          mensagem: `Olá ${proposta.instaladorNome},\n\nO cliente propôs a seguinte data para a instalação:\n\n📅 ${dataFormatada}\n\nObra: ${proposta.obraMorada || '—'}\nReferência: ${proposta.refCTR}\n\nAceda ao portal do instalador para confirmar ou sugerir outra data:\nhttps://7egv7wu65a.skywork.website/instalador\n\nCumprimentos,\nEquipa AT CONTERA`,
        });
      }
    }
  };

  const handleSaveInvoice = async () => {
    if (!lead || !invoiceInput.trim()) return;
    const updated = await updateLead(lead.id, { numeroFatura: invoiceInput.trim() });
    if (updated) setLead(updated);
    setInvoiceSaved(true);
    setTimeout(() => setInvoiceSaved(false), 3000);
  };

  const handleExtension = async () => {
    if (!lead || !extensionMotivo.trim()) return;
    await createPedidoExtensao(lead.id, extensionMotivo.trim());
    setExtensionSent(true);
  };

  const garantia = lead?.dataConclusao ? calcGarantia(lead.dataConclusao) : null;

  const estadoColor = (estado: string) => {
    if (estado === 'concluido') return 'bg-green-100 text-green-700 border-green-200';
    if (estado === 'cancelado') return 'bg-red-100 text-red-700 border-red-200';
    return 'bg-blue-100 text-blue-700 border-blue-200';
  };

  const estadoLabel: Record<string, string> = {
    novo: 'Novo', em_contacto: 'Em Contacto', orcamentos_enviados: 'Orçamentos Enviados',
    visita_agendada: 'Visita Agendada', obra_em_curso: 'Obra em Curso',
    concluido: 'Concluído', cancelado: 'Cancelado',
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/80 sticky top-0 z-10 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm">C</span>
            </div>
            <div>
              <span className="font-bold text-foreground text-base tracking-tight">AT CONTERA</span>
              <span className="text-muted-foreground text-xs block leading-none">Área do Cliente Final</span>
            </div>
          </Link>
          <Button asChild variant="ghost" size="sm">
            <Link to={ROUTE_PATHS.HOME}><ArrowLeft size={14} className="mr-1" />Início</Link>
          </Button>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-12">
        <AnimatePresence mode="wait">

          {/* SEARCH */}
          {view === 'search' && (
            <motion.div key="search" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <div className="text-center mb-10">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Shield size={32} className="text-primary" />
                </div>
                <h1 className="text-2xl font-bold text-foreground mb-2">Área do Cliente Final</h1>
                <p className="text-muted-foreground">
                  Aceda ao estado da sua garantia, submeta a fatura da obra ou solicite uma extensão.
                </p>
              </div>

              {/* Trust */}
              <div className="grid grid-cols-3 gap-3 mb-8">
                {[
                  { icon: Shield, label: 'Garantia 3 Anos' },
                  { icon: Star, label: '66 Anos Experiência' },
                  { icon: CheckCircle2, label: 'Instaladores Comelit' },
                ].map((item) => (
                  <div key={item.label} className="bg-card border border-border rounded-xl p-3 text-center">
                    <item.icon size={20} className="text-primary mx-auto mb-1" />
                    <p className="text-xs font-medium text-foreground">{item.label}</p>
                  </div>
                ))}
              </div>

              <div className="bg-card border border-border rounded-2xl p-6 shadow-sm space-y-4">
                <h2 className="font-semibold text-foreground">Aceder à minha área</h2>

                {/* Opção 1 — CTR */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Opção 1 — Código CTR</p>
                  <div className="flex gap-3">
                    <Input
                      placeholder="CTR-3F255125"
                      value={code}
                      onChange={(e) => { setCode(e.target.value.toUpperCase()); setEmailOuTel(''); }}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      className="font-mono text-base flex-1"
                    />
                    <Button onClick={handleSearch} disabled={!code.trim()}>
                      <Search size={16} className="mr-2" />Aceder
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">O código foi enviado por email após o seu pedido. Ex: <span className="font-mono">CTR-3F255125</span></p>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground">ou</span>
                  <div className="flex-1 h-px bg-border" />
                </div>

                {/* Opção 2 — Email ou Telefone */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Opção 2 — Email ou Telefone do pedido</p>
                  <div className="flex gap-3">
                    <Input
                      placeholder="email@exemplo.pt ou 912 345 678"
                      value={emailOuTel}
                      onChange={(e) => { setEmailOuTel(e.target.value); setCode(''); }}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      className="flex-1"
                    />
                    <Button onClick={handleSearch} disabled={!emailOuTel.trim()} variant="outline">
                      <Search size={16} className="mr-2" />Aceder
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">Use o email ou telefone que indicou quando fez o pedido.</p>
                </div>

                {error && (
                  <p className="text-destructive text-xs flex items-center gap-1.5 bg-destructive/5 border border-destructive/20 rounded-lg px-3 py-2">
                    <AlertTriangle size={13} />{error}
                  </p>
                )}
              </div>
            </motion.div>
          )}

          {/* DETAIL */}
          {view === 'detail' && lead && (
            <motion.div key="detail" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-4">
              <button onClick={() => setView('search')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2">
                <ArrowLeft size={14} />Pesquisar outra referência
              </button>

              {/* Header card */}
              <div className="bg-card border border-border rounded-2xl p-5">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Referência</p>
                    <p className="font-mono font-bold text-xl text-primary">CTR-{lead.id.slice(0, 8).toUpperCase()}</p>
                  </div>
                  <Badge className={`${estadoColor(lead.estado)} border text-xs`}>{estadoLabel[lead.estado]}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><p className="text-xs text-muted-foreground">Nome</p><p className="font-medium">{lead.nome}</p></div>
                  <div><p className="text-xs text-muted-foreground">Localidade</p><p className="font-medium">{lead.localidade}</p></div>
                  <div><p className="text-xs text-muted-foreground">Pedido em</p><p className="font-medium">{formatDate(lead.criadoEm)}</p></div>
                  {lead.dataConclusao && (
                    <div><p className="text-xs text-muted-foreground">Concluído em</p><p className="font-medium">{formatDate(lead.dataConclusao)}</p></div>
                  )}
                </div>
              </div>

              {/* Garantia meter */}
              {lead.estado === 'concluido' && garantia ? (
                <div className="bg-card border border-border rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Shield size={18} className="text-primary" />
                    <h3 className="font-semibold text-foreground">Estado da Garantia</h3>
                  </div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-muted-foreground">Validade</span>
                    <span className="font-semibold text-foreground">até {formatDate(garantia.expiry.toISOString())}</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-3 mb-2 overflow-hidden">
                    <div
                      className={`h-3 rounded-full transition-all ${garantia.percent > 50 ? 'bg-green-500' : garantia.percent > 20 ? 'bg-yellow-500' : 'bg-red-500'}`}
                      style={{ width: `${garantia.percent}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Concluído</span>
                    <span className="flex items-center gap-1 font-medium text-foreground">
                      <Clock size={12} />
                      {garantia.daysLeft > 0 ? `${garantia.daysLeft} dias restantes` : 'Garantia expirada'}
                    </span>
                    <span>Expira</span>
                  </div>
                </div>
              ) : lead.estado === 'cancelado' ? (
                <div className="bg-red-50 border border-red-200 rounded-2xl p-5 text-center">
                  <XCircle size={24} className="text-red-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-red-700">Pedido cancelado</p>
                  <p className="text-xs text-red-500 mt-1">Este pedido foi cancelado e não tem garantia associada.</p>
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                      <Clock size={20} className="text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-blue-800 text-sm mb-1">Estado da sua instalação</p>
                      <p className="text-xs text-blue-600 leading-relaxed mb-3">
                        Acompanhe aqui o progresso da sua instalação AT CONTERA.
                      </p>
                      {/* Progresso dinâmico */}
                      {(() => {
                        const propostaAprovada = propostas.find(p => p.estadoCliente === 'aprovado');
                        const temInstalador = !!lead.instaladorId;
                        const temProposta = propostas.length > 0;
                        const dataAgendada = propostaAprovada?.dataInstalacao;
                        const steps = [
                          { done: true, label: '📋 Pedido recebido' },
                          { done: ['em_contacto','orcamentos_enviados','visita_agendada','obra_em_curso','concluido'].includes(lead.estado), label: '👷 Instaladores contactados' },
                          { done: temInstalador, label: <span>🔧 Instalador selecionado{temInstalador ? <span className="text-blue-500 font-semibold"> · {lead.instaladorId ? (propostas[0]?.instaladorNome || '—') : '—'}</span> : null}</span> },
                          { done: temProposta, label: '📄 Orçamento enviado' },
                          { done: !!propostaAprovada, label: '✅ Orçamento aprovado por si' },
                          { done: !!dataAgendada, label: dataAgendada ? <span>📅 Instalação agendada: <span className="font-semibold">{new Date(dataAgendada).toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' })}</span></span> : '📅 Data de instalação acordada' },
                          { done: lead.estado === 'concluido', label: '🛡️ Garantia ativa' },
                        ];
                        return (
                          <div className="space-y-1.5">
                            {steps.map((step, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${step.done ? 'bg-blue-500' : 'bg-blue-100'}`}>
                                  {step.done && <CheckCircle2 size={10} className="text-white" />}
                                </div>
                                <span className={`text-xs ${step.done ? 'text-blue-700 font-medium' : 'text-blue-400'}`}>{step.label}</span>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* Fatura */}
              <div className="bg-card border border-border rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-4">
                  <FileText size={18} className="text-primary" />
                  <h3 className="font-semibold text-foreground">Número de Fatura</h3>
                </div>
                {lead.numeroFatura ? (
                  <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-3">
                    <CheckCircle2 size={18} className="text-green-600 shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Fatura registada</p>
                      <p className="font-mono font-bold text-foreground">{lead.numeroFatura}</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">Submeta o número da fatura emitida pelo instalador para ativar a garantia.</p>
                    <div className="flex gap-3">
                      <Input
                        placeholder="Ex: FT2024/0001"
                        value={invoiceInput}
                        onChange={(e) => setInvoiceInput(e.target.value)}
                        className="font-mono"
                      />
                      <Button onClick={handleSaveInvoice} disabled={!invoiceInput.trim()}>
                        {invoiceSaved ? <CheckCircle2 size={16} /> : <ChevronRight size={16} />}
                        {invoiceSaved ? 'Guardado' : 'Guardar'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Pedidos de Orçamento do Dashboard */}
              {pedidosOrc.length > 0 && (
                <div className="bg-card border border-border rounded-2xl p-5 space-y-3">
                  <div className="flex items-center gap-2">
                    <FileText size={18} className="text-primary" />
                    <h3 className="font-semibold text-foreground">Pedidos de Orçamento</h3>
                    <Badge className="text-xs bg-primary/10 text-primary border-primary/20">{pedidosOrc.length}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">A AT CONTERA está a recolher propostas de instaladores certificados na sua zona. Assim que estiverem prontas aparecem abaixo.</p>
                  <div className="space-y-2">
                    {pedidosOrc.map(p => (
                      <div key={p.id} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-foreground">{p.descricao}</p>
                          <p className="text-xs text-muted-foreground">📍 {p.zona} · {new Date(p.criadoEm).toLocaleDateString('pt-PT')}</p>
                        </div>
                        <Badge className={`text-xs border shrink-0 ${
                          p.estado === 'aberto' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                          p.estado === 'em_selecao' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                          'bg-green-100 text-green-700 border-green-200'
                        }`}>
                          {p.estado === 'aberto' ? '⏳ A recolher propostas' :
                           p.estado === 'em_selecao' ? `📋 ${p.respostas.length} proposta${p.respostas.length !== 1 ? 's' : ''} recebida${p.respostas.length !== 1 ? 's' : ''}` :
                           '✓ Concluído'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Orçamentos do cliente */}
              {propostas.length > 0 && (
                <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
                  <div className="flex items-center gap-2">
                    <FileText size={18} className="text-primary" />
                    <h3 className="font-semibold text-foreground">As suas Propostas de Orçamento</h3>
                    <Badge className="text-xs bg-primary/10 text-primary border-primary/20">{propostas.length}</Badge>
                  </div>
                  <div className="space-y-3">
                    {propostas.map(p => (
                      <div key={p.id} className={`rounded-xl border p-4 space-y-2 ${p.estadoCliente === 'aprovado' ? 'border-green-300 bg-green-50' : 'border-border bg-background'}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-sm text-foreground">{p.instaladorNome}</span>
                              <span className="text-xs text-muted-foreground">{p.tipoSistema === 'video' ? '📹 Vídeo' : '🔊 Áudio'}{p.numInquilinos ? ` · ${p.numInquilinos} inquilinos` : ''}</span>
                            </div>
                            <p className="text-xs text-muted-foreground">📅 {new Date(p.criadoEm).toLocaleDateString('pt-PT')}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-lg font-bold text-primary">{p.totalEuros.toLocaleString('pt-PT', { minimumFractionDigits: 2 })} €</p>
                            {p.estadoCliente === 'aprovado' && (
                              <Badge className="text-xs bg-green-100 text-green-700 border-green-200 mt-1">✓ Aprovado por si</Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          {p.htmlContent && (
                            <button
                              type="button"
                              onClick={() => { const w = window.open('', '_blank'); if (w) { w.document.write(p.htmlContent); w.document.close(); } }}
                              className="text-xs border border-border px-3 py-1.5 rounded-lg hover:bg-muted transition-colors"
                            >
                              👁 Ver proposta
                            </button>
                          )}
                          {p.estadoCliente !== 'aprovado' ? (
                            <button
                              type="button"
                              disabled={aprovandoProposta === p.id}
                              onClick={() => handleAprovarProposta(p.id)}
                              className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                            >
                              {aprovandoProposta === p.id ? 'A aprovar...' : '✓ Aprovar este orçamento'}
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleAprovarProposta(p.id)}
                              className="text-xs border border-border text-muted-foreground px-3 py-1.5 rounded-lg hover:bg-muted transition-colors"
                            >
                              Retirar aprovação
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {propostas.some(p => p.estadoCliente === 'aprovado') && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-800">
                      ✅ Aprovação registada. A AT CONTERA irá contactá-lo para prosseguir com a adjudicação.
                    </div>
                  )}
                  {/* Agendamento de data de instalação */}
                  {propostas.some(p => p.estadoCliente === 'aprovado') && (
                    <div className="bg-card border border-border rounded-xl p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <CalendarDays size={16} className="text-primary" />
                        <h4 className="font-semibold text-sm text-foreground">Agendar Data de Instalação</h4>
                      </div>
                      {(() => {
                        const ap = propostas.find(p => p.estadoCliente === 'aprovado');
                        if (!ap) return null;
                        if (ap.dataInstalacao) {
                          return (
                            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                              <CheckCircle2 size={16} className="text-green-600 shrink-0" />
                              <div>
                                <p className="text-xs text-muted-foreground">Data de instalação proposta</p>
                                <p className="font-semibold text-sm text-foreground">{new Date(ap.dataInstalacao).toLocaleDateString('pt-PT', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">O instalador será notificado para confirmar.</p>
                              </div>
                            </div>
                          );
                        }
                        if (agendandoData === ap.id) {
                          return (
                            <div className="space-y-2">
                              <p className="text-xs text-muted-foreground">Indique uma data preferencial para a instalação:</p>
                              <Input type="date" value={novaDataInstalacao} onChange={e => setNovaDataInstalacao(e.target.value)} min={new Date().toISOString().split('T')[0]} />
                              <div className="flex gap-2">
                                <Button size="sm" disabled={!novaDataInstalacao} onClick={() => handleAgendar(ap.id)} className="flex-1">
                                  <CalendarDays size={13} className="mr-1.5" />Confirmar data
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => { setAgendandoData(null); setNovaDataInstalacao(''); }}>Cancelar</Button>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div className="space-y-2">
                            <p className="text-xs text-muted-foreground">Proponha uma data para a instalação e o instalador será notificado para confirmar.</p>
                            <Button size="sm" variant="outline" onClick={() => setAgendandoData(ap.id)} className="w-full">
                              <CalendarDays size={13} className="mr-1.5" />Propor data de instalação
                            </Button>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}

              {/* Extensão de garantia */}
              {lead.estado === 'concluido' && (
                <div className="bg-card border border-border rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-1">
                    <Star size={18} className="text-primary" />
                    <h3 className="font-semibold text-foreground">Extensão de Garantia</h3>
                  </div>
                  {existingExtension ? (
                    <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl p-3">
                      <CheckCircle2 size={18} className="text-blue-600 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-foreground">Pedido submetido</p>
                        <p className="text-xs text-muted-foreground">Estado: {existingExtension.estado === 'pendente' ? 'Em análise' : existingExtension.estado === 'aprovado' ? 'Aprovado ✓' : 'Não aprovado'}</p>
                      </div>
                    </div>
                  ) : extensionSent ? (
                    <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-3">
                      <CheckCircle2 size={18} className="text-green-600 shrink-0" />
                      <p className="text-sm text-green-700">Pedido de extensão enviado com sucesso. Entraremos em contacto brevemente.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Caixa de oferta */}
                      <div className="rounded-xl border-2 border-primary/20 bg-primary/5 p-4">
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div>
                            <p className="font-bold text-foreground text-sm">Extensão de 2 anos adicional</p>
                            <p className="text-xs text-muted-foreground mt-0.5">Cobertura total de <strong>5 anos</strong> a contar da conclusão da obra</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-xl font-bold text-primary">240€</p>
                            <p className="text-xs text-muted-foreground">+ IVA (23%)</p>
                            <p className="text-xs font-semibold text-foreground">{(240 * 1.23).toFixed(2)}€ total</p>
                          </div>
                        </div>
                        <ul className="text-xs text-muted-foreground space-y-1">
                          <li className="flex items-center gap-1.5"><CheckCircle2 size={11} className="text-primary shrink-0" />Cobre avarias de equipamento e mão-de-obra</li>
                          <li className="flex items-center gap-1.5"><CheckCircle2 size={11} className="text-primary shrink-0" />Supervisão AT CONTERA durante toda a extensão</li>
                          <li className="flex items-center gap-1.5"><CheckCircle2 size={11} className="text-primary shrink-0" />Pagamento processado com segurança pela AT CONTERA</li>
                        </ul>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Observações (opcional)</Label>
                        <Textarea
                          placeholder="Ex: equipamento com desgaste prematuro, situação especial..."
                          className="resize-none h-20"
                          value={extensionMotivo}
                          onChange={(e) => setExtensionMotivo(e.target.value)}
                        />
                      </div>
                      <Button onClick={() => setView('extension')} className="w-full">
                        <Send size={14} className="mr-2" />Solicitar Extensão por 240€ + IVA
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {/* EXTENSION CONFIRM */}
          {view === 'extension' && lead && (
            <motion.div key="ext" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="max-w-md mx-auto text-center py-10">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5">
                <Star size={28} className="text-primary" />
              </div>
              <h2 className="text-xl font-bold text-foreground mb-2">Confirmar Extensão de Garantia</h2>
              <p className="text-muted-foreground text-sm mb-6">Vai contratar a extensão de 2 anos:</p>
              <div className="bg-muted rounded-xl p-4 text-left text-sm mb-4">
                <p className="text-xs text-muted-foreground mb-1">Referência</p>
                <p className="font-mono font-bold mb-3 text-primary">CTR-{lead.id.slice(0, 8).toUpperCase()}</p>
                <div className="flex justify-between items-center border-b border-border pb-2 mb-2">
                  <span className="text-muted-foreground">Extensão 2 anos</span><span className="font-semibold">240,00€</span>
                </div>
                <div className="flex justify-between items-center border-b border-border pb-2 mb-2">
                  <span className="text-muted-foreground">IVA 23%</span><span className="font-semibold">{(240*0.23).toFixed(2)}€</span>
                </div>
                <div className="flex justify-between items-center font-bold text-foreground">
                  <span>Total</span><span className="text-primary">{(240*1.23).toFixed(2)}€</span>
                </div>
              </div>
              {extensionMotivo.trim() && (
                <div className="bg-muted rounded-xl p-3 text-left text-sm mb-4">
                  <p className="text-xs text-muted-foreground mb-1">Observações</p>
                  <p>{extensionMotivo}</p>
                </div>
              )}
              <p className="text-xs text-muted-foreground mb-6">A AT CONTERA entrará em contacto para processar o pagamento em segurança.</p>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setView('detail')}>Cancelar</Button>
                <Button className="flex-1" onClick={() => { createPedidoExtensao(lead.id, extensionMotivo); setExtensionSent(true); setView('detail'); }}>
                  <CheckCircle2 size={14} className="mr-2" />Confirmar
                </Button>
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </div>

      <footer className="border-t border-border mt-8 py-6 text-center text-xs text-muted-foreground">
        <p>© {new Date().getFullYear()} AT CONTERA · Garantia de 3 anos · Instaladores Certificados Comelit</p>
      </footer>
    </div>
  );
}
